import React, { useState, useEffect, useRef } from 'react';
import { clsx } from 'clsx';
import { ChevronRight, ChevronDown, Plus, Edit2, Trash2, Eye, EyeOff, Tag, X, TrendingUp } from 'lucide-react';
import { tenantApi } from '../../lib/tenant-api';
import { RichTextEditor } from '../rich-text-editor';
import { FollowUpQuestionItem } from './FollowUpQuestionItem';
import type { QuestionItemProps } from './types';
import { MAX_DEPTH } from './types';

export const QuestionItem = React.memo(({
  question,
  level = 0,
  visitedPath = new Set<string>(),
  allQuestions = [],
  hierarchyPath = [],
  index = 0,
  expandedCards,
  setExpandedCards,
  displayFollowUps,
  setDisplayFollowUps,
  questionCTAs,
  getFollowUpQuestions,
  handleManageCTAs,
  handleAddFollowUp,
  handleEdit,
  handleToggleActive,
  handleDelete,
  handleRemoveCTA,
  setShowTreeView,
  availableLabels,
  handleCreateLabel,
  handleAssignLabels,
  getCTATypeColor,
  fetchQuestions,
  handleMoveUp,
  handleMoveDown,
  handleViewPerformance,
  totalSiblings,
  dropMenuMobilePos = 'right'
}: QuestionItemProps) => {
  const isCardExpanded = expandedCards.has(question.id);
  const areFollowUpsVisible = displayFollowUps.has(question.id);

  const [showOverflowMenu, setShowOverflowMenu] = useState(false);
  const [showAssignLabelDropdown, setShowAssignLabelDropdown] = useState(false);
  const [showAssignTypeDropdown, setShowAssignTypeDropdown] = useState(false);
  const [showCreateLabelInput, setShowCreateLabelInput] = useState(false);
  const [newLabelName, setNewLabelName] = useState('');
  const overflowMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showOverflowMenu && !showAssignLabelDropdown && !showAssignTypeDropdown) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (overflowMenuRef.current && !overflowMenuRef.current.contains(event.target as Node)) {
        setShowOverflowMenu(false);
        setShowAssignLabelDropdown(false);
        setShowAssignTypeDropdown(false);
        setShowCreateLabelInput(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showOverflowMenu, showAssignLabelDropdown, showAssignTypeDropdown]);

  if (level >= MAX_DEPTH) {
    return (
      <div className={`${level > 0 ? 'ml-8 mt-3' : ''}`}>
        <div className="bg-red-50 border-2 border-red-300  p-4 mx-6">
          <div className="text-sm font-medium text-red-800">⚠️ Maximum depth reached ({MAX_DEPTH} levels)</div>
          <div className="text-xs text-red-600 mt-1">Cannot expand further to prevent performance issues</div>
          <button onClick={() => setShowTreeView(true)} className="mt-2 px-2 py-1 bg-red-100 text-red-700  text-xs hover:bg-red-200">View in Tree Mode</button>
        </div>
      </div>
    );
  }

  if (visitedPath.has(question.id)) {
    const isRootCycle = allQuestions.find(q => q.id === question.id)?.is_root;
    return (
      <div className={`${level > 0 ? 'ml-8 mt-3' : ''}`}>
        <div className="bg-orange-50 border-2 border-orange-300  p-4 mx-6">
          <div className="text-sm font-medium text-orange-800">{isRootCycle ? '⤴️ Back to root question' : '🔄 Circular reference detected'}</div>
          <div className="text-xs text-orange-600 mt-1">"{question.question.substring(0, 40)}..."</div>
          <div className="text-xs text-orange-500 mt-1">This question appears earlier in the expansion path</div>
          <button onClick={() => setShowTreeView(true)} className="mt-2 px-2 py-1 bg-orange-100 text-orange-700  text-xs hover:bg-orange-200">View in Tree Mode</button>
        </div>
      </div>
    );
  }

  const followUps = getFollowUpQuestions(question.id);
  const hasFollowUps = followUps.length > 0;
  const assignedCTAs = questionCTAs[question.id] || [];
  const newVisitedPath = new Set([...visitedPath, question.id]);
  const formattedContextType = question.context_type
    ? question.context_type
      .split('_')
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
    : '';

  const toggleCard = () => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(question.id)) newSet.delete(question.id);
      else newSet.add(question.id);
      return newSet;
    });
  };

  const toggleDisplayFollowUps = () => {
    setDisplayFollowUps(prev => {
      const newSet = new Set(prev);
      if (newSet.has(question.id)) newSet.delete(question.id);
      else newSet.add(question.id);
      return newSet;
    });
  };

  // Root level (level 0)
  if (level === 0) {
    return (
      <div className="mb-4">
        <div className=" border border-indigo-200 bg-gray-100 p-4">
          <div className="flex flex-col bg-white p-4 ">
            <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center border border-indigo-200 rounded-full">
                  <span className="text-base font-medium text-indigo-600">{index + 1}</span>
                </div>
                <span
                  className={`w-2 h-2 rounded-full flex-shrink-0 ${question.is_active ? 'bg-green-400' : 'bg-gray-300'}`}
                  title={question.is_active ? 'Active' : 'Inactive'}
                />
                <button onClick={toggleCard} className="flex items-center gap-2 flex-1 min-w-0 text-left">
                  <span className={`text-base font-medium text-gray-800 ${isCardExpanded ? '' : 'line-clamp-1'}`}>{question.question}</span>
                  {isCardExpanded ? <ChevronDown className="h-4 w-4 flex-shrink-0 text-gray-800" /> : <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-800" />}
                </button>
              </div>

              <div className="flex items-center flex-wrap gap-2">
                {hasFollowUps && (
                  <button onClick={toggleDisplayFollowUps} className={`px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100  hover:bg-gray-200 flex items-center gap-1 ${areFollowUpsVisible ? 'border-b-2 border-b-indigo-600' : ''}`}>
                    <span>Display follow-ups</span><ChevronDown className={`h-3 w-3 transition-transform duration-200 ${areFollowUpsVisible ? 'rotate-180' : ''}`} />
                  </button>
                )}
                <button onClick={() => handleManageCTAs(question)} className="px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100  hover:bg-gray-200 flex items-center gap-1">
                  <span>⊕</span><span>Assign CTAs</span>
                </button>
                <button onClick={() => handleAddFollowUp(question)} className="px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100  hover:bg-gray-200 flex items-center gap-1">
                  <span>↪</span><span>Assign Follow-ups</span>
                </button>
                {(handleMoveUp || handleMoveDown) && (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => handleMoveUp?.(question)} disabled={!handleMoveUp || index === 0} className={`p-2 py-1  ${!handleMoveUp || index === 0 ? 'text-gray-300 bg-gray-50 cursor-not-allowed' : 'text-gray-700 bg-gray-100 hover:bg-gray-200'}`} title="Move up">↑</button>
                    <button onClick={() => handleMoveDown?.(question)} disabled={!handleMoveDown || index === (totalSiblings ?? 1) - 1} className={`p-2 py-1  ${!handleMoveDown || index === (totalSiblings ?? 1) - 1 ? 'text-gray-300 bg-gray-50 cursor-not-allowed' : 'text-gray-700 bg-gray-100 hover:bg-gray-200'}`} title="Move down">↓</button>
                  </div>
                )}

                <div className="relative" ref={overflowMenuRef}>
                  <button onClick={() => setShowOverflowMenu(!showOverflowMenu)} className="p-2 py-1 text-gray-700 bg-gray-100  hover:bg-gray-200">•••</button>

                  {showOverflowMenu && !showAssignLabelDropdown && !showAssignTypeDropdown && (
                    <div className={`absolute ${dropMenuMobilePos === 'right' ? 'left-auto right-0' : 'left-0 sm:left-auto sm:right-0'} mt-1 w-48 bg-white border border-gray-200  shadow-lg z-10`}>
                      <button onClick={() => { setShowOverflowMenu(false); setShowAssignLabelDropdown(true); }} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"><Tag className="h-4 w-4" /><span>Assign Label</span></button>
                      <button onClick={() => { setShowOverflowMenu(false); setShowAssignTypeDropdown(true); }} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"><Tag className="h-4 w-4" /><span>Assign Type</span></button>
                      <button onClick={() => { setShowOverflowMenu(false); handleEdit(question); }} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"><Edit2 className="h-4 w-4" /><span>Edit</span></button>
                      {question.is_baselined && handleViewPerformance && (
                        <button onClick={() => { setShowOverflowMenu(false); handleViewPerformance(question); }} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"><TrendingUp className="h-4 w-4" /><span>Performance</span></button>
                      )}
                      <button onClick={() => { setShowOverflowMenu(false); handleToggleActive(question); }} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                        {question.is_active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        <span>{question.is_active ? 'Deactivate' : 'Activate'}</span>
                      </button>
                      <button onClick={() => { setShowOverflowMenu(false); handleDelete(question.id); }} className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"><Trash2 className="h-4 w-4" /><span>Delete</span></button>
                    </div>
                  )}

                  {showAssignLabelDropdown && (
                    <div className={`absolute ${dropMenuMobilePos === 'right' ? 'left-auto right-0' : 'left-0 sm:left-auto sm:right-0'} top-full mt-1 w-52 bg-white border border-gray-200  shadow-lg z-10`}>
                      <div className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="text-sm text-gray-800 font-medium">Assign Label</div>
                          <button onClick={() => setShowAssignLabelDropdown(false)} className="text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
                        </div>
                        {!showCreateLabelInput ? (
                          <button onClick={() => setShowCreateLabelInput(true)} className="w-full text-left px-2 py-1.5 text-indigo-600 hover:bg-gray-50  flex items-center gap-2 mb-2">
                            <Plus className="h-3.5 w-3.5" /><span className="text-sm font-medium">Create Label</span>
                          </button>
                        ) : (
                          <div className="flex items-center gap-1 mb-2">
                            <input type="text" value={newLabelName} onChange={(e) => setNewLabelName(e.target.value)} placeholder="Label name" className="flex-1 min-w-0 px-2 py-1.5 border border-gray-300  text-sm" autoFocus />
                            <button onClick={async () => { if (newLabelName.trim()) { await handleCreateLabel(newLabelName); setNewLabelName(''); setShowCreateLabelInput(false); } }} className="flex-shrink-0 px-2 py-1.5 bg-indigo-600 text-white text-xs font-medium  hover:bg-indigo-700">Add</button>
                            <button onClick={() => setShowCreateLabelInput(false)} className="flex-shrink-0 text-gray-400 hover:text-gray-600"><X className="h-3.5 w-3.5" /></button>
                          </div>
                        )}
                        <div className="space-y-2 max-h-32 overflow-y-auto">
                          {availableLabels.map((lbl) => {
                            const currentLabelIds = (question.labels || []).map(l => l.id);
                            return (
                              <label key={lbl.id} className="flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" checked={currentLabelIds.includes(lbl.id)} onChange={async () => {
                                  const newIds = currentLabelIds.includes(lbl.id) ? currentLabelIds.filter(id => id !== lbl.id) : [...currentLabelIds, lbl.id];
                                  await handleAssignLabels(question.id, newIds);
                                }} className="h-4 w-4 text-indigo-600 border-gray-300 " />
                                <span className="text-sm text-gray-700">{lbl.name}</span>
                              </label>
                            );
                          })}
                          {availableLabels.length === 0 && <p className="text-xs text-gray-400">No labels yet. Create one above.</p>}
                        </div>
                        <div className="mt-3 flex justify-end">
                          <button onClick={() => setShowAssignLabelDropdown(false)} className="px-3 py-1.5 bg-indigo-600 text-white text-sm font-medium  hover:bg-indigo-700">Done</button>
                        </div>
                      </div>
                    </div>
                  )}

                  {showAssignTypeDropdown && (
                    <div className={`absolute ${dropMenuMobilePos === 'right' ? 'left-auto right-0' : 'left-0 sm:left-auto sm:right-0'} top-full mt-1 w-52 bg-white border border-gray-200  shadow-lg z-10`}>
                      <div className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="text-sm text-gray-800 font-medium">Assign Type</div>
                          <button onClick={() => setShowAssignTypeDropdown(false)} className="text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
                        </div>
                        <div className="space-y-2">
                          {(['is_root', 'is_faq', 'is_conversion', 'is_optimizable', 'is_first_class'] as const).map(field => (
                            <label key={field} className="flex items-center gap-2 cursor-pointer">
                              <input type="checkbox" checked={!!question[field]} onChange={async () => {
                                try { await tenantApi.updatePrimerQuestion(question.id, { [field]: !question[field] }); await fetchQuestions(); } catch (err) { console.error(err); }
                              }} className="h-4 w-4 text-indigo-600 border-gray-300 " />
                              <span className="text-sm text-gray-700">{field === 'is_root' ? 'Root Question' : field === 'is_faq' ? 'FAQ Question' : field === 'is_conversion' ? 'Conversion Question' : field === 'is_optimizable' ? 'Include In LARS' : 'First Class'}</span>
                            </label>
                          ))}
                        </div>
                        <div className="mt-3 flex justify-end">
                          <button onClick={() => setShowAssignTypeDropdown(false)} className="px-3 py-1.5 bg-indigo-600 text-white text-sm font-medium  hover:bg-indigo-700">Done</button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {isCardExpanded && (
              <div className="mt-4 space-y-4">
                <div>
                  <div className=" bg-gray-100">
                    <RichTextEditor value={question.answer} onChange={() => { }} hideToolbar defaultPreviewMode height="500px" minHeight="150px" />
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {question.is_root && <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">Root question</span>}
                  {question.is_optimizable && <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">Include In LARS</span>}
                  {question.is_first_class && <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-medium">First Class</span>}
                  {question.context_type && (
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                      {formattedContextType}
                    </span>
                  )}
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${question.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                    {question.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                {assignedCTAs.length > 0 && (
                  <div>
                    <div className="text-xs text-gray-600 mb-2">Assigned CTAs:</div>
                    <div className="flex flex-wrap gap-2">
                      {assignedCTAs.map((cta) => (
                        <div key={cta.id} className="flex items-center gap-2 px-3 py-1 bg-gray-50  border border-gray-200">
                          <span className={`px-2 py-0.5  text-xs font-medium ${getCTATypeColor(cta.cta_type)}`}>
                            {cta.cta_type === 'link' ? 'Link' : cta.cta_type === 'generic' ? 'Generic' : cta.cta_type}
                          </span>
                          <span className="text-sm">{cta.text}</span>
                          <button onClick={() => handleRemoveCTA(question.id, cta.id)} className="text-gray-400 hover:text-red-600">×</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {areFollowUpsVisible && hasFollowUps && (
            <div className="mt-3 space-y-3">
              {followUps.map((followUp, idx) => (
                <FollowUpQuestionItem
                  key={followUp.id}
                  question={followUp}
                  level={1}
                  parentNumber={`${index + 1}`}
                  index={idx}
                  visitedPath={newVisitedPath}
                  allQuestions={allQuestions}
                  onDisplayFollowUps={(questionId) => {
                    setDisplayFollowUps(prev => {
                      const newSet = new Set(prev);
                      if (newSet.has(questionId)) newSet.delete(questionId);
                      else newSet.add(questionId);
                      return newSet;
                    });
                  }}
                  onManageCTAs={handleManageCTAs}
                  onAddFollowUp={handleAddFollowUp}
                  onEdit={handleEdit}
                  onToggleActive={handleToggleActive}
                  onDelete={handleDelete}
                  onRemoveCTA={handleRemoveCTA}
                  getFollowUpQuestions={getFollowUpQuestions}
                  questionCTAs={questionCTAs}
                  displayFollowUps={displayFollowUps}
                  availableLabels={availableLabels}
                  onCreateLabel={handleCreateLabel}
                  onAssignLabels={handleAssignLabels}
                  getCTATypeColor={getCTATypeColor}
                  fetchQuestions={fetchQuestions}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Follow-up levels (level 1+) - fallback
  return (
    <div className="mb-3">
      <div className={clsx(
        "bg-white  border shadow-sm p-4",
        level === 1 && "border-l-4 border-l-indigo-500 bg-indigo-50",
        level === 2 && "border-l-4 border-l-purple-500 bg-purple-50",
        level >= 3 && "border-l-4 border-l-pink-500 bg-pink-50"
      )}>
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className={clsx(
              "flex-shrink-0 rounded-full flex items-center justify-center text-white font-semibold",
              level === 1 && "w-8 h-8 bg-indigo-600",
              level >= 2 && "w-7 h-7 bg-purple-600"
            )}>
              <span className="text-xs">{index + 1}</span>
            </div>
            <span
              className={`w-2 h-2 rounded-full flex-shrink-0 ${question.is_active ? 'bg-green-400' : 'bg-gray-300'}`}
              title={question.is_active ? 'Active' : 'Inactive'}
            />
            <button onClick={toggleCard} className="flex items-center gap-2 flex-1 min-w-0 text-left">
              <span className="font-medium text-gray-900">{question.question}</span>
              {isCardExpanded ? <ChevronDown className="h-4 w-4 flex-shrink-0 text-gray-400" /> : <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-400" />}
            </button>
          </div>
          <div className="flex items-center flex-wrap gap-2">
            {hasFollowUps && (
              <button onClick={toggleDisplayFollowUps} className={`px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300  hover:bg-gray-50 flex items-center gap-1 ${areFollowUpsVisible ? 'border-b-2 border-b-indigo-600' : ''}`}>
                <span>Display follow-up</span><ChevronDown className={`h-3 w-3 transition-transform duration-200 ${areFollowUpsVisible ? 'rotate-180' : ''}`} />
              </button>
            )}
            <button onClick={() => handleManageCTAs(question)} className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300  hover:bg-gray-50 flex items-center gap-1">⊕ Assign CTAs</button>
            <button onClick={() => handleAddFollowUp(question)} className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300  hover:bg-gray-50 flex items-center gap-1">↪ Assign Follow-ups</button>
            {handleMoveUp && <button onClick={() => handleMoveUp(question)} disabled={index === 0} className={`p-1.5  ${index === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-100'}`} title="Move up">↑</button>}
            {handleMoveDown && <button onClick={() => handleMoveDown(question)} disabled={index === (totalSiblings ?? 1) - 1} className={`p-1.5  ${index === (totalSiblings ?? 1) - 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-100'}`} title="Move down">↓</button>}
            <div className="relative">
              <button onClick={() => setShowOverflowMenu(!showOverflowMenu)} className="p-1.5 text-gray-600 hover:bg-gray-100 ">•••</button>
              {showOverflowMenu && (
                <div className="absolute left-0 sm:left-auto sm:right-0 mt-1 w-48 bg-white border border-gray-200  shadow-lg z-10">
                  <button onClick={() => { setShowOverflowMenu(false); handleEdit(question); }} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50">✏️ Edit</button>
                  <button onClick={() => { setShowOverflowMenu(false); handleToggleActive(question); }} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50">{question.is_active ? '⊗ Deactivate' : '✓ Activate'}</button>
                  <button onClick={() => { setShowOverflowMenu(false); handleDelete(question.id); }} className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50">🗑️ Delete</button>
                </div>
              )}
            </div>
          </div>
        </div>

        {isCardExpanded && (
          <div className="mt-4 space-y-4">
            <div>
              <div className="text-xs font-medium text-gray-700 mb-2">Answer:</div>
              <div className="border border-gray-200  bg-gray-50">
                <RichTextEditor value={question.answer} onChange={() => { }} hideToolbar defaultPreviewMode height="500px" minHeight="150px" />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {hasFollowUps && <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-medium">{followUps.length} follow-up{followUps.length !== 1 ? 's' : ''}</span>}
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${question.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>{question.is_active ? 'Active' : 'Inactive'}</span>
            </div>
            {assignedCTAs.length > 0 && (
              <div>
                <div className="text-xs text-gray-600 mb-2">Assigned CTAs:</div>
                <div className="flex flex-wrap gap-2">
                  {assignedCTAs.map((cta) => (
                    <div key={cta.id} className="flex items-center gap-2 px-3 py-1 bg-gray-50  border border-gray-200">
                      <span className={`px-2 py-0.5  text-xs font-medium ${getCTATypeColor(cta.cta_type)}`}>{cta.cta_type === 'link' ? 'Link' : cta.cta_type === 'generic' ? 'Generic' : cta.cta_type}</span>
                      <span className="text-sm">{cta.text}</span>
                      <button onClick={() => handleRemoveCTA(question.id, cta.id)} className="text-gray-400 hover:text-red-600">×</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {areFollowUpsVisible && hasFollowUps && (
        <div className="ml-3 sm:ml-8 mt-3 space-y-3">
          {followUps.map((followUp, idx) => (
            <QuestionItem
              key={followUp.id}
              question={followUp}
              level={level + 1}
              visitedPath={newVisitedPath}
              allQuestions={allQuestions}
              hierarchyPath={[...hierarchyPath, question.id]}
              index={idx}
              expandedCards={expandedCards}
              setExpandedCards={setExpandedCards}
              displayFollowUps={displayFollowUps}
              setDisplayFollowUps={setDisplayFollowUps}
              questionCTAs={questionCTAs}
              getFollowUpQuestions={getFollowUpQuestions}
              handleManageCTAs={handleManageCTAs}
              handleAddFollowUp={handleAddFollowUp}
              handleEdit={handleEdit}
              handleToggleActive={handleToggleActive}
              handleDelete={handleDelete}
              handleRemoveCTA={handleRemoveCTA}
              setShowTreeView={setShowTreeView}
              availableLabels={availableLabels}
              handleCreateLabel={handleCreateLabel}
              handleAssignLabels={handleAssignLabels}
              getCTATypeColor={getCTATypeColor}
              fetchQuestions={fetchQuestions}
              handleMoveUp={handleMoveUp}
              handleMoveDown={handleMoveDown}
              totalSiblings={followUps.length}
            />
          ))}
        </div>
      )}
    </div>
  );
});

QuestionItem.displayName = 'QuestionItem';
